import React, { FC, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import { SizeQueriesMap, useMeasureContainerWithResizeObserver } from '../../util/responsiveness';
import { useTheme } from '../../util/theme';
import useOpenCloseState from '../../util/useOpenCloseState';
import useScrollToTop from '../../util/useScrollToTop';
import FocusCatcher from '../FocusCatcher';

import {
  defaultHeaderHeight,
  defaultBannerHeight as defaultNotificationBannerHeight,
  getClassNames,
  noScrollClass,
} from './PageLayout.styles';
import { PageLayoutProps, PageLayoutWidth, isExternallyControlledContentSidebarPanel } from './PageLayout.types';
import PageLayoutBanner from './PageLayoutBanner';
import PageLayoutFooter from './PageLayoutFooter';
import PageLayoutHeader from './PageLayoutHeader';
import PageLayoutLeftSidebar from './PageLayoutLeftSidebar';
import PageLayoutPrivateContext from './PageLayoutPrivateContext';

export const query: SizeQueriesMap<PageLayoutWidth> = {
  small: { maxWidth: 700 },
  medium: { minWidth: 701, maxWidth: 1044 },
  large: { minWidth: 1045, maxWidth: 1304 },
  xlarge: { minWidth: 1305 },
};

/**
 * Wrapper for 1, 2 and 3 column layouts.
 */
// eslint-disable-next-line max-lines-per-function
const PageLayout: FC<PageLayoutProps> = (props) => {
  const {
    children,
    closeContentSidebarAriaLabel,
    collapseLeftSidebar = false,
    fullWidthLayout = false,
    isPanelExternallyControlled = false,
    noScroll = false,
    noSpacing = false,
    supressNoScrollRightPadding = false,
    onPageLayoutContentRendered,
    onRenderCollapsiblePanel,
    onRenderHeader,
    onRenderHeroBanner,
    onRenderFooter,
    onRenderBanner,
    onRenderLeftSidebar,
    onRenderSubHeader,
    resetState,
    showContentSidebarAriaLabel,
    suppressTopPadding = false,
  } = props;

  useScrollToTop(resetState);
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  let showContentPanelButtonForWidths;

  let {
    isOpen: isContentSidebarPanelOpen,
    close: closeContentSidebarPanel,
    open: openContentSidebarPanel,
  } = useOpenCloseState();

  if (isExternallyControlledContentSidebarPanel(props)) {
    isContentSidebarPanelOpen = props.isPanelOpen;
    closeContentSidebarPanel = props.closePanel;
    openContentSidebarPanel = props.openPanel;
    showContentPanelButtonForWidths = props.showContentPanelButtonForWidths;
  }

  const { size: width } = useMeasureContainerWithResizeObserver<PageLayoutWidth>({ elementReference: ref, query });
  const bodyAttributes = useMemo(() => (noScroll ? { class: noScrollClass } : {}), [noScroll]);
  const headerContent = useMemo(() => (onRenderHeader ? onRenderHeader(width) : null), [onRenderHeader, width]);
  const footerContent = useMemo(() => (onRenderFooter ? onRenderFooter() : null), [onRenderFooter]);
  const leftSidebarContent = useMemo(() => (onRenderLeftSidebar ? onRenderLeftSidebar() : null), [onRenderLeftSidebar]);

  const hasHeader = !!headerContent;
  const hasSubHeader = !!onRenderSubHeader;
  const hasNotificationBanner = !!onRenderBanner;
  const hasLeftSidebar = !!leftSidebarContent;
  const hasFooter = !!footerContent;
  const isSmall = width === PageLayoutWidth.Small;
  const showLeftSidebar = !!width && !collapseLeftSidebar && !isSmall;
  const showSubHeader = hasSubHeader && !showLeftSidebar;
  const suppressDropShadow = (hasSubHeader && showSubHeader) || hasNotificationBanner;
  const headerHeight = hasHeader ? defaultHeaderHeight : 0;
  const bannerHeight = hasNotificationBanner ? defaultNotificationBannerHeight : 0;
  const pageLayoutContentTopOffset = headerHeight + bannerHeight;

  const heroBanner = useMemo(
    () => (onRenderHeroBanner ? onRenderHeroBanner({ hasHeader: hasHeader || hasSubHeader, noSpacing, width }) : null),
    [onRenderHeroBanner, hasHeader, hasSubHeader, noSpacing, width]
  );
  const hasHeroBanner = !!heroBanner;

  const classNames = getClassNames({
    contentTopOffset: pageLayoutContentTopOffset,
    fullWidthLayout,
    noSpacing,
    supressNoScrollRightPadding,
    showLeftSidebar,
    hasHeroBanner,
    theme,
    width,
  });

  const header = headerContent ? (
    <PageLayoutHeader suppressDropShadow={suppressDropShadow}>{headerContent}</PageLayoutHeader>
  ) : null;

  const notificationBanner = hasNotificationBanner ? (
    <PageLayoutBanner hasHeader={hasHeader} width={width} onRenderBanner={onRenderBanner} />
  ) : null;

  const collapsiblePanel = useMemo(
    () =>
      onRenderCollapsiblePanel ? (
        <div className={classNames.collapsiblePanelContainer}>{onRenderCollapsiblePanel(width)}</div>
      ) : null,
    [onRenderCollapsiblePanel, classNames.collapsiblePanelContainer, width]
  );

  const leftSidebar =
    showLeftSidebar && hasLeftSidebar ? (
      <PageLayoutLeftSidebar fullWidthLayout={fullWidthLayout} topOffset={pageLayoutContentTopOffset} width={width}>
        {leftSidebarContent}
      </PageLayoutLeftSidebar>
    ) : null;

  const footer = hasFooter && <PageLayoutFooter>{footerContent}</PageLayoutFooter>;

  return (
    <PageLayoutPrivateContext.Provider
      value={{
        closeContentSidebarAriaLabel,
        collapseLeftSidebar,
        closePanel: closeContentSidebarPanel,
        fullWidthLayout,
        hasBanner: hasNotificationBanner,
        hasHeader,
        hasFooter,
        hasLeftSidebar,
        isPanelExternallyControlled,
        isPanelOpen: isContentSidebarPanelOpen,
        noScroll,
        noSpacing,
        onPageLayoutContentRendered,
        onRenderSubHeader,
        openPanel: openContentSidebarPanel,
        showContentPanelButtonForWidths,
        showLeftSidebar,
        showContentSidebarAriaLabel,
        showSubHeader,
        suppressTopPadding,
        topOffset: pageLayoutContentTopOffset,
        width,
        ref,
      }}
    >
      <Helmet bodyAttributes={bodyAttributes} defer={false} />
      <div className={classNames.pageLayout} ref={ref}>
        {header}
        {notificationBanner}
        {heroBanner}
        <div className={classNames.contentWrapper}>
          {collapsiblePanel}
          {leftSidebar}
          <div className={classNames.contentColumn}>
            <FocusCatcher state={resetState}>{children}</FocusCatcher>
          </div>
        </div>
        {footer}
      </div>
    </PageLayoutPrivateContext.Provider>
  );
};

export default PageLayout;
